import { type CSSProperties, isValidElement, useEffect, useRef } from 'react';

import classNames from 'classnames';

import { getBackgroundDarkenStyle } from '../utils/background';

import { BackgroundImage } from '../backgroundImage';
import styles from './Root.css';
import type { RootElementProps } from './Root.types';

export function RootElement(props: RootElementProps) {
  const { element, children, className, style, attributes } = props;

  const {
    align,
    vertical_align,
    backgroundDarken,
    overlayColor,
    backgroundVideo,
    vertical_snap_scroll,
    customAttributes,
  } = element;
  const { backgroundColor = 'inherit' } = element.attrs || {};

  let bgImage: any;
  if (element?.backgroundImage) {
    bgImage = <BackgroundImage src={element.backgroundImage} darken={backgroundDarken} overlayColor={overlayColor} />;
  }

  // Since backgroundVideo is inside `source`, updating it doesn't
  // trigger a re-render. So we need to use a ref to load the video.
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    videoRef.current?.load();
  }, [backgroundVideo]);

  return (
    <div
      {...customAttributes}
      className={classNames(
        className,
        styles.rootElement,
        {
          [styles.alignLeft]: align === 'left',
          [styles.alignCenter]: align === 'center',
          [styles.alignRight]: align === 'right',
          [styles.alignTop]: vertical_align === 'align_start',
          [styles.alignMiddle]: vertical_align === 'align_center',
          [styles.alignBottom]: vertical_align === 'align_end',
          [styles.verticalSnapScroll]: !!vertical_snap_scroll,
        },
        customAttributes?.className,
      )}
      style={
        {
          minHeight: vertical_align ? '100vh' : undefined,
          '--background-color': backgroundColor,
          ...style,
        } as CSSProperties
      }
      {...attributes}
    >
      {isValidElement(bgImage) && <div className={styles.background}>{bgImage}</div>}
      {backgroundVideo && (
        <div className={styles.background}>
          <video
            ref={videoRef}
            className={classNames(styles.backgroundVideo)}
            style={getBackgroundDarkenStyle(backgroundDarken)}
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={backgroundVideo} />
          </video>
        </div>
      )}
      {children}
    </div>
  );
}
